import {
  faCircle,
  faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { StepSidebar } from "src/cases/common/StepSidebar";
import {
  StepId,
  useCurrentStep,
  useSteps,
} from "src/cases/templates/formSteps";
import { stepHasErrors } from "src/cases/templates/formUtils/errorNavigation";
import { useInvalidReferenceSteps } from "src/cases/templates/formUtils/InvalidReferenceContext";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { Icon, IconVariant } from "src/design-system/Icon";

export const CaseTemplateFormSidebar: React.FC = () => {
  const [currentStepId, setCurrentStep] = useCurrentStep();
  const steps = useSteps();
  const { formState } = useFormContext<CaseTemplateFormValues>();
  const invalidRefSteps = useInvalidReferenceSteps();

  return (
    <StepSidebar
      ariaLabel="Case template form steps"
      currentStepId={currentStepId}
      steps={steps}
    >
      {(step, index) => {
        const hasErrors =
          stepHasErrors(formState.errors, step.id as StepId) ||
          invalidRefSteps.has(step.id as StepId);

        return (
          <StepButton
            active={currentStepId === step.id}
            completed={false}
            disabled={false}
            hasErrors={hasErrors}
            stepNumber={index + 1}
            totalSteps={steps.length}
            onClick={() => setCurrentStep(step.id)}
          >
            {step.title}
          </StepButton>
        );
      }}
    </StepSidebar>
  );
};

const StepButton: React.FC<{
  active: boolean;
  disabled: boolean;
  completed: boolean;
  hasErrors: boolean;
  onClick: () => void;
  children: string;
  stepNumber: number;
  totalSteps: number;
}> = ({
  completed,
  active,
  disabled,
  hasErrors,
  onClick,
  children,
  stepNumber,
  totalSteps,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const getIcon = () => {
    if (hasErrors) return faCircleExclamation;
    if (completed) return faCircleCheck;
    return faCircle;
  };

  const getIconVariant = (): IconVariant => {
    if (hasErrors) return "error";
    if (completed) return "success";
    return "default";
  };

  return (
    <button
      aria-current={active ? "step" : undefined}
      aria-disabled={disabled}
      aria-label={`Step ${stepNumber} of ${totalSteps}: ${children}${completed ? " (completed)" : ""}${hasErrors ? " (has errors)" : ""}`}
      className={twMerge(
        "z-10 flex h-6 w-full items-center gap-x-2 rounded-lg px-2 transition-all duration-300",
        !disabled && !active && "cursor-pointer hover:bg-gray-50",
        disabled && "cursor-not-allowed [&>*]:opacity-50",
        "font-inter-medium-13px",
      )}
      disabled={disabled}
      type="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Icon
        aria-hidden="true"
        variant={getIconVariant()}
        icon={getIcon()}
        size="xs"
      />
      {children}
    </button>
  );
};
